import React from 'react';
import { translate, Storage, Translate } from 'react-jhipster';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import moment from 'moment';
import { enUS, ar } from 'date-fns/locale';
import { AUTHORITIES } from 'app/config/constants';

import Box from '@mui/material/Box';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import EventIcon from '@mui/icons-material/Event';
import MobileDatePicker from '@mui/lab/MobileDatePicker';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import styled from '@mui/styles/styled';

import { Button, Input, Label } from 'reactstrap';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { IBank } from 'app/shared/model/bank.model';
import { partialUpdateEntity as partialUpdateBank } from '../bank.reducer';
import { IBankAgreementDateLog } from 'app/shared/model/bank-agreement-date-log.model';
import { createEntity as createBankAgreementDateLog } from 'app/entities/bank-agreement-date-log/bank-agreement-date-log.reducer';

export type FeeCurrency = 'Transaction Currency' | 'Bank Default Currency';
type FeeCurrencyOption = Record<FeeCurrency, string>;

export interface BankGeneralSettingForm {
  agreementDate?: Date;
  trxAmountLimit?: string;
  feeCurrency?: FeeCurrency;
}

interface BankGeneralSettingProps {
  bankId: number;
  formValue?: BankGeneralSettingForm;
  submitHandler: SubmitHandler<BankGeneralSettingForm>;
}

const options: FeeCurrencyOption = {
  'Transaction Currency': 'Transaction Currency',
  'Bank Default Currency': 'Bank Default Currency',
};

const formDefault: BankGeneralSettingForm = {
  agreementDate: null,
  feeCurrency: 'Transaction Currency',
  trxAmountLimit: '0',
};

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-input': {
    textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right',
  },
});

function toEnglishDigits(str) {
  let e = '۰'.charCodeAt(0);

  // convert arabic indic digits [٠١٢٣٤٥٦٧٨٩]
  e = '٠'.charCodeAt(0);
  str = str.replace(/[٠-٩]/g, function (t) {
    return t.charCodeAt(0) - e;
  });
  return str;
}
const handlerAmountLimit = event => {
  const amountValue = event.target.value.replace(/[^0-9.]/g, '');
  return toEnglishDigits(amountValue).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export function GeneralSettingForm({ bankId, formValue = formDefault, submitHandler }: BankGeneralSettingProps) {
  const [_, setOpenCalendar] = React.useState(false);
  const account = useAppSelector(state => state.authentication.account);
  const userRoleAccessList = useAppSelector(state => state.userManagement.userRoleAccessList);

  const isUserAuthorizedToEdit = userRoleAccessList.length === 0 || userRoleAccessList.some(role => role.bankId === bankId);

  const { handleSubmit, control } = useForm<BankGeneralSettingForm>({
    mode: 'onBlur',
    defaultValues: formValue,
  });

  return (
    <form onSubmit={handleSubmit(submitHandler)} className="collapse-section">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Controller
          name="agreementDate"
          control={control}
          render={({ field }) => (
            <div>
              <Label htmlFor="agreement-date">{translate('comexApp.bank.details.agreementDate')}</Label>
              <Box className="box-datePicker">
                <LocalizationProvider locale={Storage.session.get('locale') === 'en' ? enUS : ar} dateAdapter={AdapterDateFns}>
                  <MobileDatePicker
                    {...field}
                    toolbarTitle={translate('entity.action.selectDate')}
                    okText={translate('entity.action.ok')}
                    cancelText={translate('entity.action.cancel')}
                    mask="____/__/__"
                    inputFormat="yyyy/MM/dd"
                    views={['year', 'month', 'day']}
                    renderInput={params => {
                      return (
                        <StyledTextField
                          size="small"
                          fullWidth
                          {...params}
                          id="agreement-date"
                          sx={{ backgroundColor: '#fff' }}
                          onClick={() => setOpenCalendar(true)}
                          InputProps={{
                            placeholder: 'yyyy/mm/dd',
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton edge="end" onClick={() => setOpenCalendar(true)}>
                                  <EventIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      );
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </div>
          )}
        />
        <Controller
          name="trxAmountLimit"
          control={control}
          render={({ field }) => (
            <div>
              <Label htmlFor="transaction-limit">{translate('comexApp.bank.transactionLimit')}</Label>
              <Input {...field} id="transaction-limit" onChange={e => field.onChange(handlerAmountLimit(e))} />
            </div>
          )}
        />
        <Controller
          name="feeCurrency"
          control={control}
          render={({ field }) => (
            <div>
              <Label htmlFor="fee-currency">{translate('comexApp.bank.feeCurrency')}</Label>
              <Input type="select" {...field} id="fee-currency">
                {Object.entries(options).map(([value, display]) => (
                  <option key={value} value={value}>
                    {display}
                  </option>
                ))}
              </Input>
            </div>
          )}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            color="primary"
            disabled={
              (account?.authorities?.includes(AUTHORITIES.ALSHMOKH_USER) && !account?.authorities?.includes(AUTHORITIES.SUBADMIN_BRANCH)) ||
              !isUserAuthorizedToEdit
            }
          >
            <Translate contentKey="comexApp.bank.details.update" />
          </Button>
        </Box>
      </Box>
    </form>
  );
}

function Local({ bankId }: { bankId: number }) {
  const bankEntity = useAppSelector(state => state.bank.entity);
  const dispatch = useAppDispatch();

  const update: SubmitHandler<BankGeneralSettingForm> = data => {
    const dateStr = moment(data.agreementDate).format('MM-DD-yyyy').toLocaleString();
    const newAgreementDate = dateStr === 'Invalid date' ? null : dateStr;
    const payload: IBank = {
      id: bankId,
      localAgreementDate: newAgreementDate,
      localTransactionAmountLimit: parseFloat(data.trxAmountLimit.replace(/,/g, '')),
      localFeeCurrency: data.feeCurrency,
    };
    dispatch(partialUpdateBank(payload));

    if (newAgreementDate !== (bankEntity?.localAgreementDate ?? null)) {
      const logEntity: IBankAgreementDateLog = {
        agreementDate: newAgreementDate,
        oldValue: bankEntity?.localAgreementDate ?? null,
        commodity: 'local',
        bankId,
      };
      dispatch(createBankAgreementDateLog(logEntity));
    }
  };
  const localDateValue = () => {
    if (!bankEntity?.localAgreementDate || bankEntity?.localAgreementDate?.length === 0) {
      return null;
    }
    return moment(bankEntity?.localAgreementDate, 'MM-DD-yyyy').toDate();
  };

  const amount = String(bankEntity?.localTransactionAmountLimit || 0);

  const formValue: BankGeneralSettingForm = {
    agreementDate: localDateValue(),
    trxAmountLimit: toEnglishDigits(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    feeCurrency: (bankEntity?.localFeeCurrency as FeeCurrency) || 'Transaction Currency',
  };

  return <GeneralSettingForm bankId={bankId} formValue={formValue} submitHandler={update} />;
}

export const BankGeneralSettingLocal = React.memo(Local);

function International({ bankId }: { bankId: number }) {
  const bankEntity = useAppSelector(state => state.bank.entity);
  const dispatch = useAppDispatch();

  const internationalDateValue = () => {
    if (!bankEntity?.agreementDate || bankEntity?.agreementDate?.length === 0) {
      return null;
    }
    return moment(bankEntity?.agreementDate, 'MM-DD-yyyy').toDate();
  };

  const amount = String(bankEntity?.transactionAmountLimit || 0);

  const formValue: BankGeneralSettingForm = {
    agreementDate: internationalDateValue(),
    trxAmountLimit: toEnglishDigits(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    feeCurrency: (bankEntity?.feeCurrency as FeeCurrency) || 'Transaction Currency',
  };

  const update: SubmitHandler<BankGeneralSettingForm> = data => {
    const dateStr = moment(data.agreementDate).format('MM-DD-yyyy').toLocaleString();
    const newAgreementDate = dateStr === 'Invalid date' ? null : dateStr;
    const payload: IBank = {
      id: bankId,
      agreementDate: newAgreementDate,
      transactionAmountLimit: parseFloat(data.trxAmountLimit.replace(/,/g, '')),
      feeCurrency: data.feeCurrency,
    };
    dispatch(partialUpdateBank(payload));

    if (newAgreementDate !== (bankEntity?.agreementDate ?? null)) {
      const logEntity: IBankAgreementDateLog = {
        agreementDate: newAgreementDate,
        oldValue: bankEntity?.agreementDate ?? null,
        commodity: 'international',
        bankId,
      };
      dispatch(createBankAgreementDateLog(logEntity));
    }
  };

  return <GeneralSettingForm bankId={bankId} formValue={formValue} submitHandler={update} />;
}

export const BankGeneralSettingInternational = React.memo(International);
